/**
 * 어드민 메뉴 노출 설정 컨텍스트
 *
 * app_settings의 `admin_menu_visibility` 한 행을 앱 로드당 1회 읽어
 * Sidebar와 '메뉴 관리' 페이지가 함께 쓴다.
 * 로딩 중이거나 조회에 실패하면 빈 override(= 코드 기본값)로 동작한다.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import {
  MENU_VISIBILITY_SETTING_KEY,
  parseOverrides,
  type MenuVisibilityOverrides,
} from '@/components/nav-visibility'

interface AppSettingRow {
  key: string
  value: unknown
}

interface MenuVisibilityContextValue {
  overrides: MenuVisibilityOverrides
  isLoading: boolean
  /** 저장 성공 직후 즉시 반영 (사이드바 깜빡임 없이) */
  applyOverrides: (next: MenuVisibilityOverrides) => void
  refetch: () => void
}

const MenuVisibilityContext = createContext<MenuVisibilityContextValue | null>(null)

export function MenuVisibilityProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useApiFetch<AppSettingRow[]>('/api/admin/settings?category=SYSTEM')
  const [overrides, setOverrides] = useState<MenuVisibilityOverrides>({})

  useEffect(() => {
    if (!data) return
    setOverrides(parseOverrides(data.find((row) => row.key === MENU_VISIBILITY_SETTING_KEY)?.value))
  }, [data])

  return (
    <MenuVisibilityContext value={{ overrides, isLoading, applyOverrides: setOverrides, refetch }}>
      {children}
    </MenuVisibilityContext>
  )
}

export function useMenuVisibility(): MenuVisibilityContextValue {
  const ctx = useContext(MenuVisibilityContext)
  if (!ctx) throw new Error('useMenuVisibility must be used within MenuVisibilityProvider')
  return ctx
}
